/**
 * Settings deep-link anchors — the one place a `SettingsNav` search result (or a
 * cross-section jump) is turned into a scroll position.
 *
 * Anchors are explicit: `[data-settings-anchor="<slug>"]`, or an element whose
 * `id` is that same slug (a field that already owns an `id` for its
 * `<label htmlFor>` is addressable without a second attribute). There is
 * deliberately no text-substring fallback — the previous implementation walked
 * `label|h2|h3|h4|p|span|button` for the shortest element *containing* the
 * visible copy, which was silently wrong about as often as it was right.
 *
 * Two callers need identical behaviour: `SettingsPage` (nav + search) and
 * `SecuritySection`'s API-key jump, which changes section and anchor at once.
 * They had drifted — the second copy skipped slug validation and could not
 * cancel a previous flash — so the logic lives here instead.
 */

/** The scroll container `SettingsPage` renders around the active panel. */
export const SETTINGS_PANEL_ID = 'settings-panel'

/**
 * How long to wait after a section switch before resolving an anchor: the panel
 * mounts behind `SettingsPage`'s 150ms exit + enter transition, so the target
 * does not exist in the DOM on the frame the nav is clicked.
 */
export const SETTINGS_PANEL_MOUNT_MS = 320

/**
 * Classes for the post-scroll flash. An outline rather than a background tint:
 * it never collides with a card's own surface colour and it follows whatever
 * radius the target already has. Not a focus indicator (§13) — it names a
 * location for ~a second and is then removed again.
 */
export const FLASH_CLASSES = ['outline', 'outline-2', 'outline-offset-4', 'outline-[rgba(94,106,210,0.45)]']
export const FLASH_MS = 900

/**
 * Anchor slugs are minted in `SettingsNav` — lowercase kebab, nothing else.
 * Anything outside that shape is not a key we own, so it never reaches a
 * selector. This is also what keeps a malformed anchor from throwing a
 * `SyntaxError` out of `querySelector('#...')`.
 */
const ANCHOR_SLUG = /^[a-z][a-z0-9-]*$/

let flashTimer: number | null = null
let flashedEl: HTMLElement | null = null

/** Flash one element at a time — a second deep-link cancels the first. */
function flash(el: HTMLElement) {
  if (flashTimer !== null) {
    window.clearTimeout(flashTimer)
    flashedEl?.classList.remove(...FLASH_CLASSES)
  }
  flashedEl = el
  el.classList.add(...FLASH_CLASSES)
  flashTimer = window.setTimeout(() => {
    el.classList.remove(...FLASH_CLASSES)
    flashTimer = null
    flashedEl = null
  }, FLASH_MS)
}

/**
 * Scroll to a deep-linked field inside the mounted panel and flash it.
 *
 * Returns false when the slug is malformed or has no anchor in this panel, so
 * the caller can reset to the top rather than leave the user mid-scroll.
 */
export function scrollToSettingsAnchor(anchor: string): boolean {
  const root = document.getElementById(SETTINGS_PANEL_ID)
  if (!root) return false
  const slug = anchor.trim()
  if (!ANCHOR_SLUG.test(slug)) return false
  const el =
    root.querySelector<HTMLElement>(`[data-settings-anchor="${slug}"]`) ||
    root.querySelector<HTMLElement>(`#${slug}`)
  if (!el) return false
  // An id anchor lands on the control itself; its wrapper carries the label too.
  const field = el.closest('div') || el
  field.scrollIntoView({ behavior: 'smooth', block: 'center' })
  flash(field)
  return true
}

/**
 * Fallback when no anchor was requested, or the requested one is not wired in
 * this panel. The settings page is the scroll unit — nothing else on it scrolls
 * — so top of page is top of panel.
 */
export function scrollSettingsPanelToTop() {
  window.scrollTo({ top: 0, behavior: 'smooth' })
}

/**
 * Reveal an anchor, degrading to the top of the panel. Returns whether the
 * anchor itself was found. Callers must already have waited
 * `SETTINGS_PANEL_MOUNT_MS` if they also changed section.
 */
export function revealSettingsAnchor(anchor: string | null | undefined): boolean {
  if (anchor && scrollToSettingsAnchor(anchor)) return true
  scrollSettingsPanelToTop()
  return false
}

/** Test seam — drops the module-level flash bookkeeping between cases. */
export function __resetSettingsAnchorFlash() {
  if (flashTimer !== null) window.clearTimeout(flashTimer)
  flashedEl?.classList.remove(...FLASH_CLASSES)
  flashTimer = null
  flashedEl = null
}
